import { HistoryRounded as HistoryRoundedIcon, Info, InfoFilled as InfoIcon, SplitIcon } from 'components/icons';
import { usePostHog } from '@posthog/react';
import StatusChip from 'components/@extended/StatusChip';
import { buttonStyle } from 'components/buttons/buttonStyle';
import PermissionButton from 'components/buttons/PermissionButton';
import { EntityAuditTrailDrawer } from 'pages/audit-trail/entity/EntityAuditTrailDrawer';
import { INVOICE_AUDIT_TRAIL } from 'pages/invoices/audit-trail/invoiceAuditTrail';
import ExternalIntegrationLink from 'components/ExternalIntegrationLink';
import FeatureFlagged from 'components/featureflag/FeatureFlagged.tsx';
import { LearningTourLauncher } from 'components/learning-mode/LearningTourLauncher';
import { useLearningHints } from 'components/learning-mode/useLearningHints';
import Loader from 'components/Loader';
import Unmasked from 'components/Unmasked';
import ConfirmationModal from 'components/modals/confirmation/ConfirmationModal';
import MoneyDisplay from 'components/util/MoneyDisplay';
import { DIALOG_720, DIALOG_960 } from 'components/modals/dialogWidths';
import AddCaseToJointInvoiceModal from 'components/modals/invoices/AddCaseToJointInvoiceModal.tsx';
import EmptyInvoicePromptModal, {
    canInvoiceTransitionToPostponed,
    DELETE_EMPTY_INVOICE_PERMISSION,
    isEmptyInvoicePromptEligibleType,
    POSTPONE_EMPTY_INVOICE_PERMISSION,
} from 'components/modals/invoices/EmptyInvoicePromptModal.tsx';
import {
    markEmptyInvoicePromptShown,
    wasEmptyInvoicePromptShownRecently,
} from 'components/modals/invoices/hooks/emptyInvoicePromptStorage';
import MergeInvoiceModal from 'components/modals/invoices/MergeInvoiceModal.tsx';
import { useModal } from 'components/modals/useModal';
import PageHeader from 'components/PageHeader';
import { Alert, AlertDescription, AlertTitle } from 'components/ui/alert';
import { Button } from 'components/ui/button';
import { Tooltip } from 'components/ui/tooltip';
import { commentParam } from 'config.ts';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import PaddedLayout from 'layout/MainLayout/PaddedLayout';
import isEmpty from 'lodash/isEmpty';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FieldErrors, FormProvider, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'store';
import { useAddInvoiceMattersMutation, useGetInvoiceTimecardsAndExpensesQuery } from 'store/api/invoice-details';
import {
    useCreateCorrectionInvoiceMutation,
    useCreateCreditInvoiceMutation,
    useDeleteInvoiceMutation,
    useGetExistingPrepaymentsQuery,
    useGetInvoiceTotalsQuery,
    useMergeInvoiceMutation,
    useReissueDebitInvoiceMutation,
    useSplitInvoiceMutation,
    useUpdateInvoiceLimitedMutation,
    useUpdateInvoiceMutation,
    useUpdateInvoiceTemplateMutation,
} from 'store/api/invoices';
import { useIsNetvisorIntegrationEnabledQuery } from 'store/api/netvisor-integration';
import { FeatureFlag } from 'types/features';
import { InvoiceCommentsButton } from 'components/invoice-comments/InvoiceCommentsButton';
import { commentsRoom, useCommentsDock, useElementWidth } from 'components/invoice-comments/commentsDock';
import { useCommentsSession } from 'components/invoice-comments/commentsSession';
import { InvoiceCommentsColumn } from 'components/invoice-comments/InvoiceCommentsColumn';
import { InvoiceCommentsDrawer } from 'components/invoice-comments/InvoiceCommentsDrawer';
import useAuth from 'hooks/useAuth';
import { useIsMobile } from 'hooks/useIsMobile';
import { InvoiceData, InvoiceForm, InvoiceStatusType } from 'types/invoices';
import { dismissAlert, errorAlert, successAlert, warningAlert } from 'utils/alert';
import { getClassifierText, joinNames, t } from 'utils/getTranslations';
import { logError } from 'utils/monitoring';
import { useHasPermissions } from 'utils/permissionUtil.ts';
import { useClassifiers } from 'utils/useClassifiers';
import { useInvoiceWithRestricted } from 'utils/useInvoiceWithRestricted';
import { useGoBack, useLeaveDeletedEntity } from 'utils/useNavigateWithReturnState';
import { rejectedSubmitHandler } from 'utils/useRejectedSubmitCapture';
import { useAddNewInvoiceItems } from './hooks/useAddNewInvoiceItems';
import { useInvoice } from './hooks/useInvoice';
import { useInvoiceRecipientsFromPayer } from './hooks/useInvoiceRecipientsFromPayer';
import { SplitInvoiceIcon } from './InvoiceTableCells.tsx';
import { formatShareRate } from './payers/paymentShare';
import { usePaymentDistributionModal } from './payers/usePaymentDistributionModal';
import { MANAGE_INVOICE_PAYERS_PERMISSION, useSharedPayment } from './payers/useSharedPayment';
import { SharedPaymentIncompleteAlert } from './payers/SharedPaymentIncompleteAlert';
import { getInvoiceLearningHints } from './invoiceLearningHints';
import InvoiceWorkflow from './InvoiceWorkflow';
import CenterBlock from './layout/CenterBlock';
import SummaryDrawer from './layout/SummaryDrawer';
import { InvoiceRecordPage } from './layout/record/InvoiceRecordPage';
import { AddNewTimecardsButton } from './layout/views/components/AddNewTimecardsButton';
import { BillingInvoicingInfoAccordions } from './layout/views/components/BillingInvoicingInfoAccordions';
import {
    INVOICE_FORM_POST_SAVE_RESET_OPTIONS,
    mapInvoiceDataToInvoiceFormDto,
    mapInvoiceFormToInvoiceUpdateDto,
    mapInvoiceFormToUpdateInvoiceLimitedDto,
} from './util/InvoicesPageUtil';
import {
    getInvoiceTotalsAmounts,
    getInvoiceTypeLetter,
    isInvoiceEffectivelyEmpty,
    isInvoiceWithoutItems,
} from './util/invoiceUtil.tsx';

export type DetailsTabSuccessMessages = {
    newTimecardsAdded: boolean;
    newExpensesAdded: boolean;
    hourlyRateUpdated: boolean;
};

const AUTO_SAVE_VALIDATION_ALERT_ID = 'invoice-auto-save-validation';
const READ_COMMENTS_PERMISSION = ['READ.INVOICES.COMMENTS'];

const InvoicePage = () => {
    const { id: invoiceId } = useParams();
    const { search, pathname, state: locationState } = useLocation();
    const navigate = useNavigate();
    const goBack = useGoBack('/invoices');
    const leaveDeleted = useLeaveDeletedEntity('/invoices');
    const isNew = useIsNewDesign();
    const intl = useIntl();
    const { openModal, closeModal } = useModal();
    const parsedInvoiceId = Number(invoiceId);
    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({
        codes: [
            'INVOICE_TEMPLATE_TYPE',
            'CURRENCY',
            'INVOICE_ACCOUNTING_TYPE',
            'INVOICE_PAYMENT_TYPE',
            'EXPENSE_TYPE',
            'INVOICE_ROW_TYPE',
            'INVOICE_TYPE',
            'INVOICE_STATUS_TYPE',
            'PRICING_LIMITATION_TYPE',
            'TIMECARD_ACTIVITY_TYPE',
            'MULTIPLIER_TYPE',
            'VAT_CALCULATION_TYPE',
            'REMUNERATION_CALC_TYPE',
            'COUNTRY',
            'RECEIVABILITY_TYPE',
            'NEXT_DEBT_COLLECTION_ACTION_TYPE',
            'TIMECARD_VALUE_FACTORS',
        ],
    });

    const [addedInvoiceTimecardIds, setAddedInvoiceTimecardIds] = useState<number[]>([]);
    const [addedInvoiceExpenseIds, setAddedInvoiceExpenseIds] = useState<number[]>([]);

    // Set details tab success messages on adding new timecards and expenses
    const [successMessages, setSuccessMessages] = useState<DetailsTabSuccessMessages>({
        newTimecardsAdded: false,
        newExpensesAdded: false,
        hourlyRateUpdated: false,
    });

    // Sync newly created timecard IDs forwarded via navigation state. Both single- and
    // multi-author flows pass `newTimecardIds` so every created TC gets highlighted and the
    // success-message count is accurate. Updating state during render (guarded so the loop
    // terminates) avoids the cascading re-render that an effect would cause.
    const navigationTimecardIds = ((locationState as { newTimecardIds?: number[] } | null)?.newTimecardIds ??
        []) as number[];
    const missingNavigationIds = navigationTimecardIds.filter((id) => !addedInvoiceTimecardIds.includes(id));
    if (missingNavigationIds.length > 0) {
        setAddedInvoiceTimecardIds((prev) => [...prev, ...missingNavigationIds]);
        setSuccessMessages((prev) => ({ ...prev, newTimecardsAdded: true }));
    }
    // The same for an expense the create dialog added to the invoice (`newExpenseIds`).
    const navigationExpenseIds = (locationState as { newExpenseIds?: number[] } | null)?.newExpenseIds ?? [];
    const missingNavigationExpenseIds = navigationExpenseIds.filter((id) => !addedInvoiceExpenseIds.includes(id));
    if (missingNavigationExpenseIds.length > 0) {
        setAddedInvoiceExpenseIds((prev) => [...prev, ...missingNavigationExpenseIds]);
        setSuccessMessages((prev) => ({ ...prev, newExpensesAdded: true }));
    }

    const addNewInvoiceItems = useAddNewInvoiceItems({
        setAddedInvoiceTimecardIds,
        setAddedInvoiceExpenseIds,
        setSuccessMessages,
    });

    const { buttonsDisabled } = useSelector((state) => state.buttons);

    const {
        data: invoiceData,
        isLoading: isLoadingInvoice,
        isFetching: isFetchingInvoice,
    } = useInvoiceWithRestricted(invoiceId);

    const [updateInvoice, { isLoading: isSavingInvoice }] = useUpdateInvoiceMutation();
    const [updateInvoiceLimited, { isLoading: isSavingInvoiceLimited }] = useUpdateInvoiceLimitedMutation();
    const [updateInvoiceTemplate, { isLoading: isSavingInvoiceTemplate }] = useUpdateInvoiceTemplateMutation();
    const [deleteInvoice, { isLoading: isInvoiceDeleting }] = useDeleteInvoiceMutation();
    const [splitInvoice, { isLoading: isInvoiceCopying }] = useSplitInvoiceMutation();
    const [mergeInvoice, { isLoading: isInvoiceMerging }] = useMergeInvoiceMutation();
    const [addInvoiceMatters, { isLoading: isAddingCases }] = useAddInvoiceMattersMutation();

    const handleOnAddCases = useCallback(
        (matterIds: number[]) => {
            if (!invoiceId || !matterIds.length) {
                return;
            }
            addInvoiceMatters({ invoiceId: Number(invoiceId), matterIds })
                .unwrap()
                .then(() => successAlert(t('invoices.cases-added')))
                .catch((error) => {
                    console.error(error);
                    errorAlert(error?.data?.message ?? t('invoices.error-adding-cases'));
                });
        },
        [invoiceId, addInvoiceMatters]
    );

    const handleOnMergeInvoices = useCallback(
        (invoiceIds: number[]) => {
            if (!invoiceId || !invoiceIds.length) {
                return;
            }

            try {
                mergeInvoice({
                    invoiceId: Number(invoiceId),
                    invoiceIds,
                })
                    .unwrap()
                    .then((resp) => {
                        if (resp.id) navigate(`/invoice/${resp.id}`);
                    })
                    .catch(console.error);
            } catch (e) {
                console.error('onMergeInvoices error: ', e);
            }
        },
        [invoiceId, mergeInvoice, navigate]
    );

    const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
    const { data: isNetvisorEnabled } = useIsNetvisorIntegrationEnabledQuery();
    const handleStatusLoadingState = useCallback((isLoading: boolean) => {
        setAddedInvoiceTimecardIds([]);
        setAddedInvoiceExpenseIds([]);
        setSuccessMessages({
            newTimecardsAdded: false,
            newExpensesAdded: false,
            hourlyRateUpdated: false,
        });
        setIsUpdatingStatus(isLoading);
    }, []);

    const posthog = usePostHog();
    const [createCreditInvoice] = useCreateCreditInvoiceMutation();
    const [reissueDebitInvoice] = useReissueDebitInvoiceMutation();
    const [createCorrectionInvoice] = useCreateCorrectionInvoiceMutation();

    const handleCreateCreditInvoice = useCallback(async (): Promise<void> => {
        if (!invoiceId) {
            return;
        }

        await createCreditInvoice({ invoiceId })
            .unwrap()
            .then((resp: InvoiceData) => {
                posthog.capture('invoice_created', { type: 'credit' });
                navigate(`/invoice/${resp.id}`);
            })
            .catch((e) => console.error(e));
    }, [invoiceId, createCreditInvoice, navigate, posthog]);

    const handleCreateCorrectionInvoice = useCallback(async (): Promise<void> => {
        if (!invoiceId) {
            return;
        }

        await createCorrectionInvoice({ invoiceId })
            .unwrap()
            .then((resp: InvoiceData) => {
                posthog.capture('invoice_created', { type: 'correction' });
                navigate(`/invoice/${resp.id}`);
            })
            .catch((e) => console.error(e));
    }, [invoiceId, createCorrectionInvoice, navigate, posthog]);

    const handleReissueDebitInvoice = useCallback(
        async (fromInvoiceId: number): Promise<void> => {
            await reissueDebitInvoice({ invoiceId: fromInvoiceId })
                .unwrap()
                .then((resp: InvoiceData) => {
                    posthog.capture('invoice_created', { type: 'reissue' });
                    navigate(`/invoice/${resp.id}`);
                })
                .catch((e) => console.error(e));
        },
        [reissueDebitInvoice, navigate, posthog]
    );

    const [searchExistingPrepaymentsText, setSearchExistingPrepaymentsText] = useState('');
    const searchBarSelectors = ['invoiceNumber'];
    const { data: existingPrepayments } = useGetExistingPrepaymentsQuery(
        {
            id: invoiceData?.id || 0,
            filters: `${
                searchExistingPrepaymentsText
                    ? `(${searchBarSelectors
                          .map((selector) => `${selector}=lk="${searchExistingPrepaymentsText}"`)
                          .join(',')})`
                    : ''
            }`,
        },
        { skip: !invoiceData || invoiceData?.isRestricted }
    );

    const { data: invoiceTotals } = useGetInvoiceTotalsQuery(parsedInvoiceId, {
        skip: !parsedInvoiceId || isNaN(parsedInvoiceId) || !invoiceData || invoiceData?.isRestricted,
    });

    const { data: invoiceTimecardsAndExpenses } = useGetInvoiceTimecardsAndExpensesQuery(parsedInvoiceId, {
        skip: !parsedInvoiceId || isNaN(parsedInvoiceId) || !invoiceData || invoiceData?.isRestricted,
    });

    const canDeleteEmptyInvoice = useHasPermissions([DELETE_EMPTY_INVOICE_PERMISSION]);
    const canPostponeEmptyInvoice =
        useHasPermissions([POSTPONE_EMPTY_INVOICE_PERMISSION]) &&
        canInvoiceTransitionToPostponed(invoiceData?.invoiceStatusTypeCode, invoiceData?.invoiceTypeCode);
    const canShowEmptyPrompt = canDeleteEmptyInvoice || canPostponeEmptyInvoice;

    const isInvoiceEmpty = isInvoiceEffectivelyEmpty(invoiceData, invoiceTimecardsAndExpenses, invoiceTotals);
    const hasNoItemsToReview = isInvoiceWithoutItems(invoiceTimecardsAndExpenses, invoiceTotals);
    const isInvoiceContentLoaded = !!invoiceTimecardsAndExpenses && !!invoiceTotals;

    useEffect(() => {
        if (!canShowEmptyPrompt || !isInvoiceEmpty) return;
        if (!parsedInvoiceId || isNaN(parsedInvoiceId)) return;
        if (!invoiceData) return;
        if (invoiceData.invoiceStatusTypeCode === InvoiceStatusType.POSTPONED) return;
        if (!isEmptyInvoicePromptEligibleType(invoiceData.invoiceTypeCode)) return;
        if (wasEmptyInvoicePromptShownRecently(parsedInvoiceId)) return;
        markEmptyInvoicePromptShown(parsedInvoiceId);
        openModal(
            <EmptyInvoicePromptModal
                invoiceId={parsedInvoiceId}
                invoiceStatusTypeCode={invoiceData.invoiceStatusTypeCode}
                invoiceTypeCode={invoiceData.invoiceTypeCode}
                invoiceNumber={invoiceData.invoiceNumber}
                onClose={closeModal}
                onDeleted={leaveDeleted}
                alert
            />,
            closeModal
        );
        // openModal/closeModal/leaveDeleted are stable enough; intentionally omitting to avoid re-opens.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canShowEmptyPrompt, isInvoiceEmpty, parsedInvoiceId]);

    useEffect(() => {
        if (!invoiceTotals || invoiceTotals.cachedPaymentCurrencyTotalWithVat === undefined) {
            return;
        }

        const cached = invoiceTotals.cachedPaymentCurrencyTotalWithVat;
        const calculated = invoiceTotals.paymentCurrency?.invoice?.totalWithVat;

        if (calculated !== undefined && cached !== calculated) {
            const currencyCode = invoiceTotals.paymentCurrency?.currencyTypeCode || '';
            const formattedCached = intl.formatNumber(cached, {
                style: 'currency',
                currency: currencyCode || 'EUR',
            });

            errorAlert(t('invoices.potential-invoice-issue-cached-total', { formattedCached }), {
                duration: Infinity,
            });
        }
    }, [invoiceTotals, intl]);

    const isLoading = isInvoiceDeleting || isInvoiceCopying || isInvoiceMerging || isUpdatingStatus || isAddingCases;

    const canUserEdit = invoiceData?.isEditable ?? false;
    const canUserDelete = invoiceData?.isDeletable ?? false;
    const canUserEditTemplate = invoiceData?.isTemplateEditable ?? false;

    const initialInvoiceData = useMemo<InvoiceForm | undefined>(() => {
        if (invoiceData && classifiers) {
            return mapInvoiceDataToInvoiceFormDto(invoiceData, classifiers);
        }
        return undefined;
    }, [invoiceData, classifiers]);

    const [isCommentsOpen, setIsCommentsOpen] = useState(false);
    const [linkedCommentId, setLinkedCommentId] = useState<number | null>(null);
    const commentsButton = useRef<HTMLButtonElement | null>(null);
    const canSeeComments = useHasPermissions(READ_COMMENTS_PERMISSION) && !isNaN(parsedInvoiceId);
    const commentsSession = useCommentsSession(parsedInvoiceId);
    const revealComment = commentsSession.setRevealed;

    // Docked, the comments are a column of the invoice page; the drawer stands in for it while there is no room.
    const { user } = useAuth();
    const { docked, rememberDocked, width: columnWidth, rememberWidth } = useCommentsDock(user?.id);
    const [invoiceRowWidth, invoiceRow] = useElementWidth<HTMLDivElement>();
    const isPhone = useIsMobile();
    const room = commentsRoom(invoiceRowWidth, window.innerWidth, isNew);
    const canDock = room.hasRoom && !isPhone;
    const commentsHost = !isCommentsOpen ? null : docked && canDock ? 'column' : 'drawer';
    const [focusOnShow, setFocusOnShow] = useState<'heading' | 'dock' | null>(null);

    // Docking on, an invoice with room opens with its comments docked, once: closing them keeps them closed.
    const autoOpenFor = docked && canDock && canSeeComments ? parsedInvoiceId : null;
    const [autoOpened, setAutoOpened] = useState<number | null>(null);
    if (autoOpenFor !== null && autoOpened !== autoOpenFor) {
        setAutoOpened(autoOpenFor);
        setFocusOnShow(null);
        setIsCommentsOpen(true);
    }

    const openComments = useCallback(() => setIsCommentsOpen(true), []);

    const closeComments = useCallback(() => {
        setIsCommentsOpen(false);
        setLinkedCommentId(null);
        setAutoOpened(parsedInvoiceId);
        commentsSession.reset();
    }, [parsedInvoiceId, commentsSession]);

    // While docking is on the Comments button opens and closes the column, and leaves docking on.
    const toggleComments = () => {
        if (commentsHost === 'column') {
            closeComments();
            return;
        }
        setFocusOnShow(docked && canDock ? 'heading' : null);
        openComments();
    };
    const dockComments = () => {
        setFocusOnShow('dock');
        // Already open here: the auto-open would draw the column with no focus to hand it.
        setAutoOpened(parsedInvoiceId);
        rememberDocked(true);
    };
    const undockComments = () => {
        setFocusOnShow('dock');
        rememberDocked(false);
    };
    const closeColumn = () => {
        rememberDocked(false);
        closeComments();
        commentsButton.current?.focus();
    };

    const [isAuditTrailOpen, setIsAuditTrailOpen] = useState(false);

    // While an auto-save is in flight or the invoice is refetching after one, the cached invoice can
    // be older than the form state — applying it would flash previous values on the fields that were
    // just edited. Hold form syncing (values: undefined skips it) until the data has settled;
    // keepDirtyValues additionally protects unsaved edits when a sync does run.
    const isInvoiceDataSettled =
        !isSavingInvoice && !isSavingInvoiceLimited && !isSavingInvoiceTemplate && !isFetchingInvoice;
    const formMethods = useForm<InvoiceForm>({
        values: isInvoiceDataSettled ? initialInvoiceData : undefined,
        defaultValues: initialInvoiceData,
        resetOptions: { keepDirtyValues: true },
    });

    const { getValues } = formMethods;

    const saveRecipientsFromPayer = useCallback(() => {
        if (!invoiceId || isNaN(Number(invoiceId))) {
            return;
        }
        void updateInvoiceLimited(mapInvoiceFormToUpdateInvoiceLimitedDto(getValues(), Number(invoiceId)))
            .unwrap()
            .then(() => {
                formMethods.reset(getValues(), INVOICE_FORM_POST_SAVE_RESET_OPTIONS);
            })
            .catch(console.error);
    }, [invoiceId, updateInvoiceLimited, getValues, formMethods]);

    // The new design's cards hold their fields until Save; classic has none and saves each field as it changes.
    const [isCardEditing, setIsCardEditing] = useState(false);
    useInvoiceRecipientsFromPayer({
        formMethods,
        persistedInvoiceForm: initialInvoiceData,
        isSavingInvoice: isSavingInvoice || isSavingInvoiceLimited,
        saveRecipients: saveRecipientsFromPayer,
        isHeld: isCardEditing,
    });

    // Reset form values when switching to view mode
    useEffect(() => {
        if (!canUserEdit) {
            formMethods.reset();
        }
    }, [canUserEdit, formMethods]);

    // A comment notification links here with the comment's id: the comments open on that entry.
    useEffect(() => {
        const params = new URLSearchParams(search);
        const commentId = Number(params.get(commentParam));
        if (!params.has(commentParam)) {
            return;
        }
        if (canSeeComments && commentId > 0) {
            setLinkedCommentId(commentId);
            revealComment(commentId);
            setFocusOnShow(null);
            openComments();
        }
        params.delete(commentParam);
        const newSearch = params.toString();
        navigate(`${pathname}${newSearch ? '?' + newSearch : ''}`, { replace: true });
    }, [search, pathname, navigate, canSeeComments, openComments, revealComment]);

    const {
        isCredit,
        isDebit,
        isSent,
        isCreditable,
        isSplittable,
        isDeletable,
        isMergeable,
        isCorrectable,
        isPartiallyCredited,
        isCorrection,
        isPaid,
    } = useInvoice(invoiceData);
    const { isSharedPaymentCopy } = useSharedPayment(invoiceData);
    const openPaymentDistribution = usePaymentDistributionModal(invoiceData);
    const canManageInvoicePayers = useHasPermissions([MANAGE_INVOICE_PAYERS_PERMISSION]);

    const handleOpenMergeInvoiceModal = useCallback(
        (invoice: InvoiceData) => {
            openModal(
                <MergeInvoiceModal invoice={invoice} onClose={closeModal} onSubmit={handleOnMergeInvoices} />,
                closeModal,
                { width: 900 },
                undefined,
                DIALOG_720
            );
        },
        [openModal, closeModal, handleOnMergeInvoices]
    );

    const handleOpenAddCaseModal = useCallback(
        (invoice: InvoiceData) => {
            openModal(
                <AddCaseToJointInvoiceModal
                    invoiceId={invoice.id}
                    invoiceNumber={invoice.invoiceNumber}
                    onClose={closeModal}
                    onSubmit={handleOnAddCases}
                />,
                closeModal,
                { width: 900 },
                undefined,
                DIALOG_960
            );
        },
        [openModal, closeModal, handleOnAddCases]
    );

    const invoiceStatus = invoiceData?.invoiceStatusTypeCode as InvoiceStatusType | undefined;
    const learningHints = useMemo(
        () => (invoiceStatus ? getInvoiceLearningHints(invoiceStatus, isNew) : []),
        [invoiceStatus, isNew]
    );
    const { isActive: isLearningModeActive, startTour: startLearningTour } = useLearningHints(learningHints, {
        ready: isInvoiceContentLoaded,
    });

    if (!invoiceData || isLoadingInvoice) {
        return <Loader />;
    }

    if (invoiceData.isRestricted) {
        return (
            <PaddedLayout>
                <PageHeader
                    title={`Invoice nr. ${invoiceData.invoiceNumber}`}
                    titleNode={<Unmasked>{`Invoice nr. ${invoiceData.invoiceNumber}`}</Unmasked>}
                    showBreadcrumb
                />
                {isNew ? (
                    // The title stands where the band would, the notice in card weight under it; nothing else.
                    <Alert className="bg-card shadow-card mt-4 w-fit max-w-[44rem] border-transparent">
                        <Info className="text-warning-ink" />
                        <AlertTitle>
                            <FormattedMessage id="access-restricted" />
                        </AlertTitle>
                        <AlertDescription>{invoiceData.restrictedAccessMessage}</AlertDescription>
                    </Alert>
                ) : (
                    <div className="my-4">
                        <Alert className="bg-paper text-fg flex items-center gap-2 rounded-md border-transparent py-2 pr-4 pl-3 text-sm leading-[1.125rem] font-semibold">
                            <span className="flex w-6 shrink-0 justify-center [&>svg]:size-4">
                                <InfoIcon className="text-warning" />
                            </span>
                            <div className="w-full min-w-0">
                                <p className="text-subtitle1 mb-[0.35em] font-bold">
                                    <FormattedMessage id="access-restricted" />
                                </p>
                                <p className="text-body2 font-normal">{invoiceData.restrictedAccessMessage}</p>
                            </div>
                        </Alert>
                    </div>
                )}
            </PaddedLayout>
        );
    }

    if (isLoadingClassifiers || !initialInvoiceData || isEmpty(formMethods.formState.defaultValues || {})) {
        return <Loader />;
    }

    const onAutoSaveValidationError = (errors: FieldErrors<InvoiceForm>) => {
        const errorFields = Object.keys(errors).join(', ');
        warningAlert(t('invoices.error-auto-save-validation', { fields: errorFields }), {
            id: AUTO_SAVE_VALIDATION_ALERT_ID,
        });
    };

    const submitInvoice = async (formData: InvoiceForm) => {
        let isSaved = false;
        try {
            if (invoiceId && !isNaN(Number(invoiceId))) {
                await updateInvoice(mapInvoiceFormToInvoiceUpdateDto(formData, Number(invoiceId)))
                    .unwrap()
                    .then(() => {
                        dismissAlert(AUTO_SAVE_VALIDATION_ALERT_ID);
                        formMethods.reset(getValues(), INVOICE_FORM_POST_SAVE_RESET_OPTIONS);
                        isSaved = true;
                    })
                    .catch(console.error);
            }
        } catch (error) {
            console.error(error);
        }
        setAddedInvoiceTimecardIds([]);
        setAddedInvoiceExpenseIds([]);
        setSuccessMessages({
            newTimecardsAdded: false,
            newExpensesAdded: false,
            hourlyRateUpdated: false,
        });
        return isSaved;
    };

    const handleUpdateInvoiceLimited = async (formData: InvoiceForm) => {
        let isSaved = false;
        try {
            if (invoiceId && !isNaN(Number(invoiceId))) {
                await updateInvoiceLimited(mapInvoiceFormToUpdateInvoiceLimitedDto(formData, Number(invoiceId)))
                    .unwrap()
                    .then(() => {
                        dismissAlert(AUTO_SAVE_VALIDATION_ALERT_ID);
                        formMethods.reset(getValues(), INVOICE_FORM_POST_SAVE_RESET_OPTIONS);
                        isSaved = true;
                    })
                    .catch(console.error);
            }
        } catch (error) {
            console.error(error);
        }
        setAddedInvoiceTimecardIds([]);
        setAddedInvoiceExpenseIds([]);
        setSuccessMessages({
            newTimecardsAdded: false,
            newExpensesAdded: false,
            hourlyRateUpdated: false,
        });
        return isSaved;
    };

    const submitTemplateUpdate = async (formData: InvoiceForm) => {
        let isSaved = false;
        try {
            if (invoiceId && !isNaN(Number(invoiceId))) {
                await updateInvoiceTemplate({
                    id: Number(invoiceId),
                    invoiceTemplate: formData.invoiceTemplate?.id ? { id: formData.invoiceTemplate.id } : null,
                    invoiceSpecTemplate: formData.invoiceSpecTemplate?.id
                        ? { id: formData.invoiceSpecTemplate.id }
                        : null,
                })
                    .unwrap()
                    .then(() => {
                        dismissAlert(AUTO_SAVE_VALIDATION_ALERT_ID);
                        formMethods.reset(getValues(), INVOICE_FORM_POST_SAVE_RESET_OPTIONS);
                        isSaved = true;
                    })
                    .catch((error) => {
                        console.error('Failed to update invoice template:', error);
                        logError(error);
                    });
            }
        } catch (error) {
            console.error('Failed to update invoice template:', error);
            logError(error);
        }
        return isSaved;
    };

    // A confirmation's title names the invoice; a draft may have no number yet.
    const confirmedInvoice = invoiceData.invoiceNumber || 'none';

    const deleteInvoiceHandler = async () => {
        try {
            if (parsedInvoiceId && !isNaN(parsedInvoiceId)) {
                const otherCopies = isSharedPaymentCopy
                    ? (invoiceData.invoiceCopies ?? []).filter((copy) => copy.id !== parsedInvoiceId)
                    : [];
                openModal(
                    <ConfirmationModal
                        onCancel={closeModal}
                        onConfirm={() => {
                            void deleteInvoice({
                                invoiceId: parsedInvoiceId,
                                sharedPaymentInvoiceIds: isSharedPaymentCopy
                                    ? otherCopies.map((copy) => copy.id)
                                    : undefined,
                            })
                                .unwrap()
                                .then(leaveDeleted)
                                .catch(console.error)
                                .finally(() => closeModal());
                        }}
                        content={<p className="text-body1">{t('invoices.alerts.delete-confirmation-text')}</p>}
                        alert={{
                            titleId: 'invoices.confirm.delete-invoice-title',
                            titleValues: { invoice: confirmedInvoice },
                            description: invoiceTotals ? (
                                <p>
                                    <FormattedMessage
                                        id="invoices.confirm.delete-invoice-text"
                                        values={{
                                            payer: invoiceData.clientParty.name,
                                            amount: (
                                                <MoneyDisplay
                                                    amount={
                                                        getInvoiceTotalsAmounts(invoiceTotals)
                                                            .totalWithVatPaymentCurrency
                                                    }
                                                    currency={invoiceTotals.paymentCurrency.currencyTypeCode}
                                                />
                                            ),
                                        }}
                                    />
                                    {otherCopies.length > 0 && (
                                        <>
                                            {' '}
                                            <FormattedMessage
                                                id="invoices.confirm.delete-shared-copies-text"
                                                values={{
                                                    count: otherCopies.length,
                                                    numbers: joinNames(otherCopies.map((copy) => copy.invoiceNumber)),
                                                }}
                                            />
                                        </>
                                    )}
                                </p>
                            ) : undefined,
                            confirmId: 'invoices.confirm.delete-invoice',
                            tone: 'danger',
                        }}
                    />
                );
            }
        } catch (e) {
            console.error('onSubmit error: ', e);
            errorAlert(t('forms.error-submitting-the-form'));
        }
    };

    const handleOnSplitInvoice = async () => {
        try {
            if (parsedInvoiceId && !isNaN(parsedInvoiceId)) {
                openModal(
                    <ConfirmationModal
                        onCancel={closeModal}
                        onConfirm={() => {
                            void splitInvoice({ invoiceId: parsedInvoiceId })
                                .unwrap()
                                .then((resp: InvoiceData) => {
                                    navigate(`/invoice/${resp.id}`);
                                })
                                .catch(console.error)
                                .finally(() => closeModal());
                        }}
                        content={<p className="text-body1">{t('invoices.alerts.split-confirmation-text')}</p>}
                        alert={{
                            titleId: 'invoices.confirm.copy-invoice-title',
                            titleValues: { invoice: confirmedInvoice },
                            description: t('invoices.confirm.copy-invoice-text'),
                            confirmId: 'copy',
                        }}
                    />
                );
            }
        } catch (e) {
            console.error('onSubmit error: ', e);
            errorAlert(t('forms.error-submitting-the-form'));
        }
    };

    const isInViewMode =
        !canUserEdit || (isCredit && isSent) || isPartiallyCredited || (isCorrection && (isSent || isPaid));

    const isJointInvoice = invoiceData.invoiceMatters && invoiceData.invoiceMatters.length > 1;
    const isSplitInvoice = invoiceData.isSplit ?? false;

    const STATUSES_ALLOWING_CASE_ADDING: string[] = [
        InvoiceStatusType.DRAFT,
        InvoiceStatusType.REVIEWED,
        InvoiceStatusType.APPROVED,
        InvoiceStatusType.FINALIZED,
    ];
    const isCaseAddable =
        (isJointInvoice || !invoiceData.matter.isJointInvoiceDisabled) &&
        isDebit &&
        !isPartiallyCredited &&
        !invoiceData.hasCreditInvoice &&
        !isSplitInvoice &&
        STATUSES_ALLOWING_CASE_ADDING.includes(invoiceData.invoiceStatusTypeCode);

    const sharedPaymentGroup = isSharedPaymentCopy ? invoiceData.sharedPaymentGroup : null;
    const sharedPaymentDeleteBlockedReason = sharedPaymentGroup?.isLocked
        ? t('invoices.payment-distribution.locked-title', { number: sharedPaymentGroup.lockedByInvoiceNumber ?? '' })
        : sharedPaymentGroup && !canManageInvoicePayers
          ? t('invoices.shared-payment.delete-needs-manage-payers')
          : null;

    const headerActions = (
        // The comments drawer lies over the header actions while it is open.
        <div className="flex flex-row justify-end gap-2" inert={commentsHost === 'drawer'}>
            {isCorrectable ? (
                <FeatureFlagged action="show" flag={FeatureFlag.INVOICE_CORRECTION_FLOW}>
                    <PermissionButton
                        permissions={[
                            'CREATE.INVOICES.MATTER_ACCESS',
                            'CREATE.INVOICES.TYPE_CORRECTION_WITH_MATTER_ACCESS',
                        ]}
                        variant="outlined"
                        color="secondary"
                        size="small"
                        onClick={() => {
                            void handleCreateCorrectionInvoice();
                        }}
                        disabled={buttonsDisabled || isPartiallyCredited}
                    >
                        <FormattedMessage id="invoices.correct" />
                    </PermissionButton>
                </FeatureFlagged>
            ) : null}
            {canUserEdit && isMergeable ? (
                <Button
                    {...buttonStyle({ variant: 'outlined', color: 'secondary' })}
                    onClick={() => handleOpenMergeInvoiceModal(invoiceData)}
                    disabled={buttonsDisabled}
                >
                    <FormattedMessage id="invoices.merge" />
                </Button>
            ) : null}
            {canUserEdit && isCaseAddable ? (
                <PermissionButton
                    permissions={['UPDATE.INVOICES.ADD_MATTER_TO_INVOICE']}
                    variant="outlined"
                    color="secondary"
                    size="small"
                    onClick={() => handleOpenAddCaseModal(invoiceData)}
                    disabled={buttonsDisabled}
                >
                    <FormattedMessage id="invoices.add-case" />
                </PermissionButton>
            ) : null}
            <AddNewTimecardsButton
                invoice={invoiceData}
                isInViewMode={isInViewMode}
                onAddNewTimecards={addNewInvoiceItems.handleAddNewTimecards}
            />
            {isSplittable ? (
                <PermissionButton
                    permissions={['UPDATE.INVOICES.COPYING']}
                    requiredFeature={FeatureFlag.INVOICE_COPYING_ENABLED}
                    variant="outlined"
                    color="secondary"
                    size="small"
                    onClick={() => {
                        void handleOnSplitInvoice();
                    }}
                    disabled={buttonsDisabled}
                >
                    <FormattedMessage id="copy" />
                </PermissionButton>
            ) : null}
            {isSharedPaymentCopy ? (
                <Button
                    {...buttonStyle({ variant: 'outlined', color: 'secondary' }, 'gap-1')}
                    onClick={openPaymentDistribution}
                    disabled={buttonsDisabled}
                >
                    <SplitIcon className="size-4" />
                    <FormattedMessage id="invoices.payment-distribution.button" />
                </Button>
            ) : null}
            {canUserDelete && isDeletable ? (
                sharedPaymentDeleteBlockedReason ? (
                    <Tooltip content={sharedPaymentDeleteBlockedReason} className="w-fit text-center">
                        <div className="w-fit">
                            <Button {...buttonStyle({ variant: 'outlined', color: 'secondary' })} disabled>
                                <FormattedMessage id="delete" />
                            </Button>
                        </div>
                    </Tooltip>
                ) : (
                    <Button
                        {...buttonStyle({ variant: 'outlined', color: 'secondary' })}
                        onClick={() => {
                            void deleteInvoiceHandler();
                        }}
                        disabled={buttonsDisabled || isPartiallyCredited}
                    >
                        <FormattedMessage id="delete" />
                    </Button>
                )
            ) : null}
            {isCredit && isSent && invoiceData.debitInvoice ? (
                <PermissionButton
                    permissions={['CREATE.INVOICES.MATTER_ACCESS', 'CREATE.INVOICES.TYPE_DEBIT_WITH_MATTER_ACCESS']}
                    variant="outlined"
                    color="secondary"
                    size="small"
                    onClick={() => {
                        void handleReissueDebitInvoice(invoiceData.debitInvoice!.id);
                    }}
                    disabled={buttonsDisabled}
                >
                    <FormattedMessage id="invoices.actions.create-new-debit" />
                </PermissionButton>
            ) : null}
            {!isNaN(parsedInvoiceId) ? (
                <PermissionButton
                    permissions={['READ.INVOICES.AUDIT_TRAIL']}
                    variant="outlined"
                    color="secondary"
                    size="small"
                    onClick={() => setIsAuditTrailOpen(true)}
                    aria-label={t('audit-trail.invoice.button')}
                    title={t('audit-trail.invoice.button')}
                    className="aspect-square min-w-0 px-0!"
                >
                    <HistoryRoundedIcon fontSize="small" />
                </PermissionButton>
            ) : null}
            {!isNaN(parsedInvoiceId) && canSeeComments ? (
                <InvoiceCommentsButton
                    invoiceId={parsedInvoiceId}
                    expanded={isCommentsOpen}
                    onOpen={toggleComments}
                    buttonRef={commentsButton}
                />
            ) : null}
            {isCreditable ? (
                <PermissionButton
                    permissions={['CREATE.INVOICES.TYPE_CREDIT_WITH_MATTER_ACCESS']}
                    variant="outlined"
                    color="secondary"
                    size="small"
                    onClick={() => {
                        void handleCreateCreditInvoice();
                    }}
                    disabled={buttonsDisabled || invoiceData.hasCreditInvoice || isPartiallyCredited}
                >
                    <FormattedMessage id="invoices.credit" />
                </PermissionButton>
            ) : null}
        </div>
    );

    const invoiceTypeCodeClassifierValue = getClassifierText(classifiers['INVOICE_TYPE'], invoiceData.invoiceTypeCode);

    const invoiceIcons = (
        <div className="text-fg-secondary flex items-center gap-2">
            {invoiceTypeCodeClassifierValue && (
                <Tooltip content={invoiceTypeCodeClassifierValue} className="w-fit text-center">
                    <div className="w-fit">
                        <StatusChip
                            className="bg-divider text-grey-a400 new:leading-[0.7778] mb-1 p-[5px] text-lg"
                            label={getInvoiceTypeLetter(invoiceData.invoiceTypeCode)}
                            type="custom"
                        />
                    </div>
                </Tooltip>
            )}
            {isSplitInvoice && !isSharedPaymentCopy && <SplitInvoiceIcon className="ml-2" />}
            {isSharedPaymentCopy && (
                <Tooltip
                    content={t('invoices.payers.shared-payment-chip-tooltip', {
                        position: String(invoiceData.sharedPaymentGroup?.position ?? 1),
                        count: String(invoiceData.sharedPaymentGroup?.invoiceCount ?? 1),
                    })}
                    className="w-fit text-center"
                >
                    <div className="ml-2 w-fit">
                        <StatusChip
                            className="bg-grey-100 text-grey-a400 mb-1 gap-1 p-[5px] text-lg"
                            type="custom"
                            icon={<SplitIcon className="size-4" />}
                            label={t('invoices.payers.shared-payment-chip', {
                                rate: formatShareRate(invoiceData.paymentShareRate),
                            })}
                        />
                    </div>
                </Tooltip>
            )}
            {isJointInvoice && (
                <div className="ml-2 w-fit">
                    <StatusChip
                        className="bg-grey-100 text-grey-a400 new:leading-[0.7778] mb-1 p-[5px] text-lg"
                        label={t('invoices.joint-invoice')}
                        type={'custom'}
                    />
                </div>
            )}
        </div>
    );

    const templateValidation = async () => {
        const values = formMethods.getValues();
        const hasInvoiceTemplate = !!values.invoiceTemplate;
        const hasInvoiceSpecTemplate = !!values.invoiceSpecTemplate;
        const isValid = hasInvoiceTemplate && hasInvoiceSpecTemplate;
        if (!isValid) {
            warningAlert(t('invoices.error-template-not-bound'));
            await Promise.all([formMethods.trigger('invoiceTemplate'), formMethods.trigger('invoiceSpecTemplate')]);
        }
        return isValid;
    };
    // Resolves whether the save landed, which a card of the new design waits on to close; classic, saving each field
    // as it changes, ignores it.
    const saveForm = async (save: (formData: InvoiceForm) => Promise<boolean>) => {
        let isSaved = false;
        await formMethods.handleSubmit(
            async (formData) => {
                isSaved = await save(formData);
            },
            rejectedSubmitHandler('invoice-edit', onAutoSaveValidationError)
        )();
        return isSaved;
    };
    // What the page hands its tabs, the classic center block's and the new design's alike.
    const tabProps = {
        invoice: invoiceData,
        classifiers,
        isInViewMode,
        addedInvoiceTimecardIds,
        addedInvoiceExpenseIds,
        setAddedInvoiceTimecardIds,
        setAddedInvoiceExpenseIds,
        successMessages,
        setSuccessMessages,
        handleStatusLoadingState,
        addNewInvoiceItems,
        isLoading,
        searchExistingPrepaymentsText,
        setSearchExistingPrepaymentsText,
        submitInvoice: () => saveForm(submitInvoice),
        submitTemplateUpdate: () => saveForm(submitTemplateUpdate),
        isTemplateEditable: canUserEditTemplate,
        updateInvoiceLimited: () => saveForm(handleUpdateInvoiceLimited),
        existingPrepayments,
    };
    const sharedPaymentIncompleteAlert =
        sharedPaymentGroup?.isComplete === false ? (
            <SharedPaymentIncompleteAlert
                group={sharedPaymentGroup}
                onOpenPaymentDistribution={openPaymentDistribution}
            />
        ) : null;

    const commentsColumn =
        commentsHost === 'column' ? (
            <InvoiceCommentsColumn
                invoiceId={parsedInvoiceId}
                linkedCommentId={linkedCommentId}
                session={commentsSession}
                width={columnWidth}
                maxWidth={room.maxWidth}
                onResize={rememberWidth}
                onUndock={undockComments}
                onClose={closeColumn}
                focusOnShow={focusOnShow}
            />
        ) : null;

    return (
        <div className="flex-1 overflow-hidden">
            <FormProvider {...formMethods}>
                {isNew ? (
                    <InvoiceRecordPage
                        {...tabProps}
                        templateValidation={templateValidation}
                        onPostponeSuccess={() => navigate('/home?tab=mypendinginvoices')}
                        isInvoiceEmpty={hasNoItemsToReview}
                        isInvoiceContentLoaded={isInvoiceContentLoaded}
                        onOpenHistory={() => setIsAuditTrailOpen(true)}
                        comments={
                            canSeeComments
                                ? {
                                      isOpen: isCommentsOpen,
                                      isDrawerOpen: commentsHost === 'drawer',
                                      onOpen: toggleComments,
                                      buttonRef: commentsButton,
                                  }
                                : undefined
                        }
                        menu={{
                            isCaseAddable,
                            deleteBlockedReason: sharedPaymentDeleteBlockedReason,
                            onMerge: () => handleOpenMergeInvoiceModal(invoiceData),
                            onAddCase: () => handleOpenAddCaseModal(invoiceData),
                            onAddNewTimecards: addNewInvoiceItems.handleAddNewTimecards,
                            onCopy: () => void handleOnSplitInvoice(),
                            onPaymentDistribution: openPaymentDistribution,
                            onCorrect: () => void handleCreateCorrectionInvoice(),
                            onCredit: () => void handleCreateCreditInvoice(),
                            onCreateNewDebit: () => {
                                if (invoiceData.debitInvoice)
                                    void handleReissueDebitInvoice(invoiceData.debitInvoice.id);
                            },
                            onDelete: () => void deleteInvoiceHandler(),
                        }}
                        notice={sharedPaymentIncompleteAlert}
                        onEditingChange={setIsCardEditing}
                        commentsColumn={commentsColumn}
                        rowRef={invoiceRow}
                    />
                ) : (
                    <PaddedLayout className="flex h-full flex-col gap-2">
                        <PageHeader
                            icons={invoiceIcons}
                            title={invoiceData?.invoiceNumber || ''}
                            titleNode={<Unmasked>{invoiceData?.invoiceNumber || ''}</Unmasked>}
                            iconsEnd={
                                isNetvisorEnabled && invoiceData.accountingIntegrationInvoiceUrl ? (
                                    <ExternalIntegrationLink
                                        href={invoiceData.accountingIntegrationInvoiceUrl}
                                        label="Netvisor"
                                    />
                                ) : null
                            }
                            showBreadcrumb
                            onBack={goBack}
                            buttons={headerActions}
                        />
                        {sharedPaymentIncompleteAlert}
                        <div ref={invoiceRow} className="flex min-h-0 flex-1">
                            <div className="@container/invoice min-w-0 flex-1 overflow-auto">
                                <div className="border-divider flex h-full overflow-hidden border-y @max-[1012px]/invoice:min-w-[960px]">
                                    <div className="border-divider w-fit max-w-min min-w-[264px] overflow-auto border-x @max-[1186px]/invoice:min-w-[240px]">
                                        <div className="px-6 py-5 @max-[1186px]/invoice:min-w-[240px] @max-[1186px]/invoice:px-4 @max-[1186px]/invoice:py-3">
                                            <InvoiceWorkflow
                                                invoice={invoiceData}
                                                handleStatusLoadingState={handleStatusLoadingState}
                                                isLoading={isLoading}
                                                templateValidation={templateValidation}
                                                onPostponeSuccess={() => navigate('/home?tab=mypendinginvoices')}
                                                isInvoiceEmpty={hasNoItemsToReview}
                                                isInvoiceContentLoaded={isInvoiceContentLoaded}
                                            />
                                        </div>
                                        <BillingInvoicingInfoAccordions invoice={invoiceData} />
                                    </div>
                                    <CenterBlock {...tabProps} />
                                    <SummaryDrawer
                                        invoice={invoiceData}
                                        isLoading={isLoading}
                                        templateValidation={templateValidation}
                                    />
                                </div>
                            </div>
                            {commentsColumn}
                        </div>
                    </PaddedLayout>
                )}
                {/* Keyed by invoice so filters chosen on one invoice do not carry over to the next. */}
                {!isNaN(parsedInvoiceId) ? (
                    <EntityAuditTrailDrawer
                        key={parsedInvoiceId}
                        trail={INVOICE_AUDIT_TRAIL}
                        entityId={parsedInvoiceId}
                        entityLabel={invoiceData?.invoiceNumber || ''}
                        open={isAuditTrailOpen}
                        onClose={() => setIsAuditTrailOpen(false)}
                        detached={isNew}
                    />
                ) : null}
                {canSeeComments ? (
                    <InvoiceCommentsDrawer
                        // Keyed by invoice like the audit trail beside it, with a prefix so the two sibling keys differ.
                        key={`comments-${parsedInvoiceId}`}
                        invoiceId={parsedInvoiceId}
                        open={commentsHost === 'drawer'}
                        onClose={closeComments}
                        returnFocus={commentsButton}
                        linkedCommentId={linkedCommentId}
                        session={commentsSession}
                        docked={docked}
                        onDock={canDock ? dockComments : undefined}
                        focusDock={focusOnShow === 'dock'}
                    />
                ) : null}
                {isLearningModeActive ? <LearningTourLauncher onStart={startLearningTour} /> : null}
            </FormProvider>
        </div>
    );
};

export default InvoicePage;
